import React, { useState } from 'react'

import {
  Box,
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material'
import clsx from 'clsx'

import { formatMonthDayAndYear } from '$extensionSrc/utils/dateUtils'

import styles from './Seats.module.scss'
import { useSorting } from '../../../utils/useSorting'

type SignupData = {
  email: string | null
  signupDate: any
  lastUsedDate?: any | null
}

export default function Seats({
  signups,
  itemsPerPage = 5,
}: {
  signups: SignupData[]
  itemsPerPage?: number
}) {
  const [currentPage, setCurrentPage] = useState(0)

  const { sortedData, handleSort, getSortIndicator } = useSorting({
    data: signups,
    defaultSortKey: 'signupDate',
    defaultDirection: 'desc',
    onSortChange: () => setCurrentPage(0), // Reset to first page on sort
  })

  const totalPages = Math.ceil(sortedData.length / itemsPerPage)
  const startIndex = currentPage * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentSignups = sortedData.slice(startIndex, endIndex)

  const handlePrevious = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1)
    }
  }

  const handleNext = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1)
    }
  }

  return (
    <Box>
      <Table className={styles.table}>
        <TableHead>
          <TableRow>
            <TableCell>Email</TableCell>
            <TableCell onClick={() => handleSort('signupDate')}>
              Signup date{getSortIndicator('signupDate')}
            </TableCell>
            <TableCell onClick={() => handleSort('lastUsedDate')}>
              Last used{getSortIndicator('lastUsedDate')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {currentSignups.map((signup, index) => (
            <TableRow
              key={`${signup.email || 'unknown'}-${signup.signupDate || index}`}
              className={index % 2 === 0 ? styles.evenRow : styles.oddRow}
            >
              <TableCell className={styles.tableCell}>
                {signup.email || <Skeleton width={150} />}
              </TableCell>
              <TableCell className={styles.tableCell}>
                {signup.signupDate ? (
                  formatMonthDayAndYear(signup.signupDate)
                ) : (
                  <Skeleton width={100} />
                )}
              </TableCell>
              <TableCell className={styles.tableCell}>
                {signup.lastUsedDate
                  ? formatMonthDayAndYear(signup.lastUsedDate)
                  : '--'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {totalPages > 1 && (
        <Box className={styles.pagination}>
          <Button
            className={styles.pageButton}
            disabled={currentPage === 0}
            onClick={handlePrevious}
          >
            {`<`}
          </Button>

          {/* Page before current */}
          {currentPage > 0 && (
            <Button
              className={styles.pageButton}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              {currentPage}
            </Button>
          )}

          {/* Current page */}
          <Button className={clsx(styles.pageButton, styles.currentPage)}>
            {currentPage + 1}
          </Button>

          {/* Page after current */}
          {currentPage + 1 < totalPages && (
            <Button
              className={styles.pageButton}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              {currentPage + 2}
            </Button>
          )}

          {/* Two pages after current */}
          {currentPage + 2 < totalPages && (
            <Button
              className={styles.pageButton}
              onClick={() => setCurrentPage(currentPage + 2)}
            >
              {currentPage + 3}
            </Button>
          )}

          <Button
            className={styles.pageButton}
            disabled={currentPage === totalPages - 1}
            onClick={handleNext}
          >
            {`>`}
          </Button>
        </Box>
      )}
    </Box>
  )
}
